import {
  CreditCard,
  Gift,
  Percent,
  Sparkles,
} from "lucide-react";
import { ReactNode } from "react";
import { formatPrice, RefProductDetailData } from "../RefProductDetailTypes";

export type DetailSection = {
  title: string;
  subtitle: string;
  rows?: {
    label: string;
    value: string;
  }[];
  bullets?: string[];
  paragraph?: string;
};

export type DetailContent = {
  storeName: string;
  pickLabel: string;
  pickText: string;
  colorLabel?: string;
  sizeLabel?: string;
  showSizeGuide?: boolean;
  offers: {
    title: string;
    text: string;
    action: string;
    icon: ReactNode;
  }[];
  policies: {
    shippingTitle?: string;
    shipping: string;
    deliveryTitle?: string;
    delivery: string;
    returnsTitle?: string;
    returns: string;
    paymentTitle?: string;
    payment: string;
    securityTitle?: string;
    security?: string;
  };
  reviewCount: string;
  detailSections: DetailSection[];
};

export function buildRefProductDetailContent(
  product: RefProductDetailData
): DetailContent {
  const voucherAmount = product.voucherAmount || 0;
  const voucherValidity = product.voucherValidityDays || 365;

  const offers = [
    ...(voucherAmount > 0
      ? [
          {
            title: `₹${formatPrice(voucherAmount)} Shopping Voucher`,
            text: `Get an instant ₹${formatPrice(voucherAmount)} Salar shopping voucher with ${voucherValidity} days validity upon purchase.`,
            action: "Voucher details",
            icon: <Gift size={20} />,
          },
        ]
      : []),
    {
      title: "Bank & Payment Offers",
      text: "Get instant discount or rewards on eligible UPI, card and net banking transactions at checkout.",
      action: "View payment offers",
      icon: <CreditCard size={20} />,
    },
    {
      title: "Referral & Repurchase Benefits",
      text: product.referralPlanName
        ? `Linked with ${product.referralPlanName} plan. Earn repurchase benefits on every active order.`
        : "Earn affiliate commissions and loyalty rewards on referral purchases.",
      action: "Referral details",
      icon: <Sparkles size={20} />,
    },
    {
      title: "Special Pricing Offer",
      text:
        product.discountPercent > 0
          ? `Save ₹${formatPrice(product.savingsAmount)} (${product.discountPercent}% OFF) off standard pricing.`
          : "Exclusive direct pricing for registered referral members.",
      action: "Price details",
      icon: <Percent size={20} />,
    },
  ];

  const detailSections: DetailSection[] = [
    {
      title: "Overview & Features",
      subtitle: "Product highlights and specifications",
      rows: [
        { label: "Brand", value: product.brand || "Salar Store" },
        { label: "Category", value: product.category || "Online / Referral" },
        { label: "Product Type", value: "Digital Referral Product" },
        { label: "SSIN", value: product.ssin || "-" },
        { label: "Product ID", value: product.productId || product.id },
        { label: "Access", value: "Instant Digital Activation" },
        ...(voucherAmount > 0
          ? [
              {
                label: "Voucher Amount",
                value: `₹${formatPrice(voucherAmount)}`,
              },
              {
                label: "Voucher Validity",
                value: `${voucherValidity} Days`,
              },
            ]
          : []),
        ...(product.autoRepurchaseAmount
          ? [
              {
                label: "Auto Repurchase",
                value: `₹${formatPrice(product.autoRepurchaseAmount)}`,
              },
            ]
          : []),
        ...(product.referralPlanName
          ? [{ label: "Referral Plan", value: product.referralPlanName }]
          : []),
      ],
      bullets:
        product.bulletPoints && product.bulletPoints.length > 0
          ? product.bulletPoints
          : undefined,
      paragraph: product.description || undefined,
    },
  ];

  if (product.attributes && Object.keys(product.attributes).length > 0) {
    detailSections.push({
      title: "Product Specifications",
      subtitle: "Technical and plan attributes",
      rows: Object.entries(product.attributes).map(([label, value]) => ({
        label,
        value: String(value),
      })),
    });
  }

  if (voucherAmount > 0) {
    detailSections.push({
      title: "Salar Shopping Voucher Benefits",
      subtitle: "How your shopping vouchers work",
      bullets: [
        `Shopping voucher worth ₹${formatPrice(voucherAmount)} is automatically credited to your account upon purchase.`,
        `Enjoy a generous ${voucherValidity}-day validity period to redeem your voucher balance.`,
        "Redeemable across a wide selection of eligible products on the Salar marketplace.",
        "Combine your voucher balances with existing daily deals and promotional discounts.",
        ...(product.autoRepurchaseAmount
          ? [
              `Auto-repurchase amount of ₹${formatPrice(
                product.autoRepurchaseAmount
              )} is reserved to keep your referral account benefits active.`,
            ]
          : []),
      ],
    });
  }

  detailSections.push({
    title: "Digital Access & Terms",
    subtitle: "Activation, redemption guidelines, and support",
    rows: [
      {
        label: "Product Format",
        value: "100% Online Digital Product",
      },
      {
        label: "Access Mode",
        value: "Instant Activation in Customer Wallet / Dashboard",
      },
      {
        label: "Sold By",
        value: product.sellerName || "Salar Verified Partner",
      },
      {
        label: "Authenticity",
        value: "Verified Genuine Referral Plan & Vouchers",
      },
      {
        label: "Support",
        value:
          product.returnPolicy ||
          "Full platform customer support & voucher assistance",
      },
    ],
  });

  return {
    storeName: product.brand || "Salar Store",
    pickLabel: "REFERRAL CHOICE",
    pickText: "Top-rated digital voucher product with verified member benefits",
    offers,
    policies: {
      deliveryTitle: "Instant Activation",
      delivery: "Instant digital access & voucher credit directly to your account",
      shippingTitle: "100% Online",
      shipping: "Digital referral product with zero physical shipping required",
      returnsTitle: "Voucher Assurance",
      returns:
        product.returnPolicy ||
        "Assured voucher validity with 24x7 customer support",
      paymentTitle: "Secure Payments",
      payment: "UPI, Cards, Netbanking & Wallets supported",
      securityTitle: "100% Authentic",
      security: "Genuine Salar referral product with buyer guarantee",
    },
    reviewCount: product.reviews || "",
    detailSections,
  };
}
